import {
	HttpErrorResponse,
	HttpInterceptorFn,
	HttpStatusCode,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, finalize } from "rxjs";
import { AuthService } from "../services/auth/auth.service";
import { SharedService } from "../services/shared/shared.service";
import { ToastService } from "../services/toast.service";

// Map backend API error codes to $localize IDs so error toasts follow the active locale.
function translateApiErrorCode(code: string | undefined): string | null {
	switch (code) {
		// Global framework errors
		case "api.global.server.error.downstream":
			return $localize`:@@api.global.server.error.downstream:Unable to process the request. Please try again later.`;
		case "api.global.server.error.internal":
			return $localize`:@@api.global.server.error.internal:Server Exception. Application could not process the request.`;
		case "api.global.request.error.internal":
			return $localize`:@@api.global.request.error.internal:An unexpected error occurred.`;
		case "api.global.request.error.twserviceunavailable":
			return $localize`:@@api.global.request.error.twserviceunavailable:Service temporarily unavailable. Please try again later.`;
		case "api.global.request.error.validation":
			return $localize`:@@api.global.request.error.validation:Invalid request.`;
		case "api.global.record.error.notFound":
			return $localize`:@@api.global.record.error.notFound:Record not found.`;
		case "api.global.access.error.unauthorized":
			return $localize`:@@api.global.access.error.unauthorized:Unauthorized access.`;
		case "api.global.client.error.access.denied":
			return $localize`:@@api.global.client.error.access.denied:Unauthorized access. User may not have enough permission to access the requested data.`;

		// Order approvals service
		case "api.orders.mpOrderApprovalsService.empOrderApproval.error.validation.alreadyProcessed":
			return $localize`:@@api.orders.mpOrderApprovalsService.empOrderApproval.error.validation.alreadyProcessed:Order is already processed.`;
		case "api.orders.mpOrderApprovalsService.empOrderApproval.error.validation.unauthorized":
			return $localize`:@@api.orders.mpOrderApprovalsService.empOrderApproval.error.validation.unauthorized:User is not authorized to approve this order`;
		case "api.orders.mpOrderApprovalsService.handleApprove.error.validation.invalidStatus":
			return $localize`:@@api.orders.mpOrderApprovalsService.handleApprove.error.validation.invalidStatus:Invalid Order status for order`;

		// Notification service
		case "api.orders.notifyService.invokeNotificationEndPoint.error.notFound":
			return $localize`:@@api.orders.notifyService.invokeNotificationEndPoint.error.notFound:Send Notification request failed. Try again after some time`;

		// Approve order
		case "api.orders.orderCardControlService.approveOrder.error.duplicateAction":
			return $localize`:@@api.orders.orderCardControlService.approveOrder.error.duplicateAction:Already request raised for the same action`;
		case "api.orders.orderCardControlService.approveOrder.error.invalidOrUnauthorized":
			return $localize`:@@api.orders.orderCardControlService.approveOrder.error.invalidOrUnauthorized:Invalid order or User is not authorized to access this merchant's order`;
		case "api.orders.orderCardControlService.approveOrder.error.invalidStatus":
			return $localize`:@@api.orders.orderCardControlService.approveOrder.error.invalidStatus:Requested Order not in valid status. Order might be approved already.`;
		case "api.orders.orderCardControlService.approveOrder.error.twApprovalFailed":
			return $localize`:@@api.orders.orderCardControlService.approveOrder.error.twApprovalFailed:Failed to approve order. Please try again later.`;

		// Manage shipment
		case "api.orders.orderCardControlService.manageShipment.error.duplicateAction":
			return $localize`:@@api.orders.orderCardControlService.manageShipment.error.duplicateAction:Already request raised for the same action`;
		case "api.orders.orderCardControlService.manageShipment.error.invalidStatus":
			return $localize`:@@api.orders.orderCardControlService.manageShipment.error.invalidStatus:Requested Order not in valid status.`;
		case "api.orders.orderCardControlService.manageShipment.error.notFound":
			return $localize`:@@api.orders.orderCardControlService.manageShipment.error.notFound:Could not locate the requested order`;
		case "api.orders.orderCardControlService.manageShipment.error.unauthorized":
			return $localize`:@@api.orders.orderCardControlService.manageShipment.error.unauthorized:User is not authorized to access this merchant's order`;

		// Refund order
		case "api.orders.orderCardControlService.refundOrder.error.duplicateAction":
			return $localize`:@@api.orders.orderCardControlService.refundOrder.error.duplicateAction:Already request raised for the same action`;
		case "api.orders.orderCardControlService.refundOrder.error.invalidStatus":
			return $localize`:@@api.orders.orderCardControlService.refundOrder.error.invalidStatus:Requested order pending for approval. Refund cannot be submitted now.`;
		case "api.orders.orderCardControlService.refundOrder.error.notFound":
			return $localize`:@@api.orders.orderCardControlService.refundOrder.error.notFound:Could not locate the requested order`;
		case "api.orders.orderCardControlService.refundOrder.error.unauthorized":
			return $localize`:@@api.orders.orderCardControlService.refundOrder.error.unauthorized:User is not authorized to access this merchant's order`;

		// Resend order (order-level)
		case "api.orders.orderCardControlService.resendOrder.error.alreadyResent":
			return $localize`:@@api.orders.orderCardControlService.resendOrder.error.alreadyResent:The order has been resent already, please try again later.`;
		case "api.orders.orderCardControlService.resendOrder.error.notFound":
			return $localize`:@@api.orders.orderCardControlService.resendOrder.error.notFound:Could not locate the requested order`;
		case "api.orders.orderCardControlService.resendOrder.error.invalidFormat":
			return $localize`:@@api.orders.orderCardControlService.resendOrder.error.invalidFormat:Resend can only be initiated for orders with physical cards.`;
		case "api.orders.orderCardControlService.resendOrder.error.unauthorized":
			return $localize`:@@api.orders.orderCardControlService.resendOrder.error.unauthorized:User is not authorized to resend this merchant's order`;
		case "api.orders.orderCardControlService.resendOrder.error.invalidStatus":
			return $localize`:@@api.orders.orderCardControlService.resendOrder.error.invalidStatus:Requested Order pending for approval. Resend cannot be submitted now.`;

		// Resend card
		case "api.orders.orderCardControlService.resendCard.error.alreadyResent":
			return $localize`:@@api.orders.orderCardControlService.resendCard.error.alreadyResent:The cards(s) have been resent already, please try again later.`;
		case "api.orders.orderCardControlService.resendCard.error.cardNotExist":
			return $localize`:@@api.orders.orderCardControlService.resendCard.error.cardNotExist:Requested Card is not exist to process`;
		case "api.orders.orderCardControlService.resendCard.error.insufficientData":
			return $localize`:@@api.orders.orderCardControlService.resendCard.error.insufficientData:Insufficient data, resendMode and destType is required to process your request`;

		// Release activation hold
		case "api.orders.orderCardControlService.releaseActivationHold.error.duplicateAction":
			return $localize`:@@api.orders.orderCardControlService.releaseActivationHold.error.duplicateAction:Already request raised for the same action`;
		case "api.orders.orderCardControlService.releaseActivationHold.error.notFound":
			return $localize`:@@api.orders.orderCardControlService.releaseActivationHold.error.notFound:Could not locate the requested order`;
		case "api.orders.orderCardControlService.releaseActivationHold.error.unauthorized":
			return $localize`:@@api.orders.orderCardControlService.releaseActivationHold.error.unauthorized:User is not authorized to access this merchant's order`;
		case "api.orders.orderCardControlService.releaseActivationHold.error.invalidStatus":
			return $localize`:@@api.orders.orderCardControlService.releaseActivationHold.error.invalidStatus:Requested order is not currently on activation hold.`;

		default:
			return null;
	}
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
	const authService = inject(AuthService);
	const sharedService = inject(SharedService);
	const toastService = inject(ToastService);
	const router = inject(Router);
	const authToken = authService.getIdToken();
	const shouldHideLoader = req.headers.get("skipLoader") === "true";
	if (!shouldHideLoader)
		// sharedService.setLoading(true);
		/*if (authToken) {*/
		console.log();
	const authReq = req.clone({
		setHeaders: {
			Authorization: `Bearer ${authToken}`,
		},
	});
	return next(authReq).pipe(
		finalize(() => sharedService.setLoading(false)),
		catchError((error) => {
			const service = authReq.url.split("/");
			const api = service[service.length - 1];
			const events = service[service.length - 2];
			const feature = service[service.length - 3];
			sharedService.setLoading(false);
			//console.error(error);
			if (error instanceof HttpErrorResponse) {
				if (error.status == HttpStatusCode.Unauthorized) {
					router.navigate(["/auth/signin"]);
				}
				const localizedByCode = translateApiErrorCode(error.error?.code);
				let errorTxt = error.message;
				if (localizedByCode) {
					errorTxt = localizedByCode;
				} else if (error.error && error.error.errors) {
					errorTxt = error.error.errors;
				} else {
					errorTxt = error.error.message;
				}
				//  if it's from chat bot never show error toast
				if (api === "prompt" || (api === 'data' && events === 'events') || (api === 'balance' && feature === 'cards')) {
				} else {
					toastService.showErrorToast($localize`:@@common.error:Error`, errorTxt);
				}
			}
			throw error;
		}),
	);
	/*} else {
        return next(req).pipe(finalize(() => sharedService.setLoading(false)));
    }*/
};
